import React, { useMemo } from 'react';

import ProductionBomEditView from '@components/shared/production_boms/ProductionBomEditView';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import { useOperatorProductService } from '@hooks/operator/products/useOperatorProductService';
import PageHeader from '@shared/PageHeader';
import useRecordMerchantSync from '@hooks/operator/useRecordMerchantSync';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import operatorProductionBomService from '@services/operator/productionBomService';

interface InventoryTypeOption {
  id: string;
  name?: string;
  [key: string]: unknown;
}

interface MerchantInventoryType {
  merchant_id: string;
  enabled?: boolean;
  inventory_type: InventoryTypeOption;
}

const OperatorProductionBomEdit: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id?: string }>();

  const { merchantInventoryTypes, currentMerchant } = useOperatorData();
  const { handleRecordLoaded, merchantDataLoading } = useRecordMerchantSync();
  const productService = useOperatorProductService();

  // currentMerchant 在 edit 模式下會被 useRecordMerchantSync 切到 record 所屬 merchant；
  // inventory types 永遠跟著 currentMerchant 走，避免顯示其他 merchant 的選項。
  const effectiveMerchantId = currentMerchant?.id ?? null;

  const inventoryTypes = useMemo(() => {
    if (!effectiveMerchantId) return [];
    return (merchantInventoryTypes as MerchantInventoryType[] | undefined ?? [])
      .filter(mit => mit.merchant_id === effectiveMerchantId && mit.enabled !== false)
      .map(mit => mit.inventory_type)
      .filter(Boolean);
  }, [merchantInventoryTypes, effectiveMerchantId]);

  return (
    <div>
      <PageHeader
        items={[
          'production',
          'production.production_boms',
          { label: t('shared.production_boms.form.edit_title') },
        ]}
      />
      <ProductionBomEditView
        productionBomService={operatorProductionBomService}
        productService={productService}
        inventoryTypes={inventoryTypes}
        role="operator"
        merchantId={effectiveMerchantId ?? ''}
        onProductionBomLoaded={id ? handleRecordLoaded : undefined}
        merchantDataLoading={merchantDataLoading}
      />
    </div>
  );
};

export default OperatorProductionBomEdit;
